/**
 * Customer Segmentation Utility
 * Classifies customers/orders as "Everyday" or "Schools & Teams" (B2B)
 */

export type CustomerSegment = "everyday" | "schools_teams";

// B2B keywords to check in shipping name/company
const B2B_KEYWORDS = [
  "school",
  "college",
  "academy",
  "trust",
  "council",
  "nhs",
  "clinic",
  "ltd",
  "limited",
  "university",
  "hospital",
  "care home",
  "nursery",
  "foundation",
  "charity",
  "centre",
  "center",
];

// B2B email domain endings
const B2B_EMAIL_DOMAINS = [
  ".sch.uk",
  ".ac.uk",
  ".gov.uk",
  ".nhs.uk",
  ".nhs.net",
];

// Product name patterns that indicate B2B orders
const B2B_PRODUCT_PATTERNS = [
  /education\s*kit/i,
  /access\s*to\s*work/i,
  /school\s*bundle/i,
  /classroom\s*set/i,
  /bulk/i,
];

// Quantity threshold for B2B classification
const B2B_QUANTITY_THRESHOLD = 10;

interface OrderItem {
  name: string;
  quantity: number;
  price: number;
}

interface DeliveryAddress {
  fullName?: string;
  organisation?: string;
  line1?: string;
  line2?: string;
  city?: string;
  postcode?: string;
  country?: string;
  [key: string]: any;
}

interface OrderLike {
  customer_email: string;
  customer_name: string;
  items: OrderItem[];
  delivery_address: DeliveryAddress;
}

interface CustomerLike {
  email: string;
  name: string;
}

/**
 * Check if an email domain indicates B2B
 */
function isB2BEmail(email: string): boolean {
  const lowerEmail = email.toLowerCase();
  
  // Check for B2B domain endings
  if (B2B_EMAIL_DOMAINS.some((domain) => lowerEmail.endsWith(domain))) {
    return true;
  }
  
  // Check if email contains "school"
  if (lowerEmail.includes("school")) {
    return true;
  }
  
  return false;
}

/**
 * Check if name/organisation contains B2B keywords
 */
function hasB2BKeywords(text: string): boolean {
  const lowerText = text.toLowerCase();
  return B2B_KEYWORDS.some((keyword) => lowerText.includes(keyword));
}

/**
 * Check if any product in the order is a B2B product
 */
function hasB2BProduct(items: OrderItem[]): boolean {
  return items.some((item) =>
    B2B_PRODUCT_PATTERNS.some((pattern) => pattern.test(item.name))
  );
}

/**
 * Check if total quantity exceeds B2B threshold
 */
function exceedsQuantityThreshold(items: OrderItem[]): boolean {
  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
  return totalQuantity >= B2B_QUANTITY_THRESHOLD;
}

/**
 * Get customer segment from an order
 */
export function getOrderSegment(order: OrderLike): CustomerSegment {
  // 1. Check email domain
  if (isB2BEmail(order.customer_email)) {
    return "schools_teams";
  }

  // 2. Check for B2B products (Education Kit, Access to Work Kit, etc.)
  if (hasB2BProduct(order.items)) {
    return "schools_teams";
  }

  // 3. Check quantity threshold
  if (exceedsQuantityThreshold(order.items)) {
    return "schools_teams";
  }

  // 4. Check shipping name/organisation for B2B keywords
  const addressName = order.delivery_address?.fullName || "";
  const organisation = order.delivery_address?.organisation || "";
  const combinedText = `${addressName} ${organisation} ${order.customer_name}`;
  
  if (hasB2BKeywords(combinedText)) {
    return "schools_teams";
  }

  // Default to Everyday
  return "everyday";
}

/**
 * Get customer segment from customer data (checks email and name)
 */
export function getCustomerSegment(customer: CustomerLike): CustomerSegment {
  // 1. Check email domain
  if (isB2BEmail(customer.email)) {
    return "schools_teams";
  }

  // 2. Check customer name for B2B keywords
  if (hasB2BKeywords(customer.name)) {
    return "schools_teams";
  }

  // Default to Everyday
  return "everyday";
}

/**
 * Get customer segment from a list of their orders
 * If ANY order qualifies as B2B, customer is B2B
 */
export function getCustomerSegmentFromOrders(
  customer: CustomerLike,
  orders: OrderLike[]
): CustomerSegment {
  // Check customer-level first
  if (getCustomerSegment(customer) === "schools_teams") {
    return "schools_teams";
  }

  // Check if any of their orders qualifies as B2B
  const customerOrders = orders.filter(
    (o) => o.customer_email.toLowerCase() === customer.email.toLowerCase()
  );

  for (const order of customerOrders) {
    if (getOrderSegment(order) === "schools_teams") {
      return "schools_teams";
    }
  }

  return "everyday";
}

/**
 * Segment display labels
 */
export const SEGMENT_LABELS: Record<CustomerSegment, string> = {
  everyday: "Everyday",
  schools_teams: "Schools & Teams",
};

/**
 * Filter orders by segment
 */
export function filterOrdersBySegment(
  orders: OrderLike[],
  segment: CustomerSegment | "all"
): OrderLike[] {
  if (segment === "all") return orders;
  return orders.filter((order) => getOrderSegment(order) === segment);
}

/**
 * Get segment counts from orders
 */
export function getSegmentCounts(orders: OrderLike[]): {
  everyday: number;
  schools_teams: number;
} {
  let everyday = 0;
  let schools_teams = 0;

  for (const order of orders) {
    if (getOrderSegment(order) === "schools_teams") {
      schools_teams++;
    } else {
      everyday++;
    }
  }

  return { everyday, schools_teams };
}
